import { useState, useEffect } from "react";
import axios from "axios";
import { API, useAuth } from "../App";
import ReactECharts from "echarts-for-react";
import {
  Wallet,
  ArrowRightLeft,
  PiggyBank,
  Banknote,
  TrendingUp,
  CheckCircle
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const FundsDashboard = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const { token } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed-projects`, {}, { headers: { Authorization: `Bearer ${token}` } });
        const response = await axios.get(`${API}/projects`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setProjects(response.data.projects || []);
      } catch (error) {
        console.error("Error fetching funds data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  // Calculate funds data from projects
  const totalBudget = projects.reduce((acc, p) => acc + (p.budget_total || 0), 0);
  const totalSpent = projects.reduce((acc, p) => acc + (p.budget_spent || 0), 0);
  const availableFunds = totalBudget - totalSpent;

  const formatCurrency = (val) => {
    if (val >= 1000000000) return `$${(val / 1000000000).toFixed(2)}B`;
    if (val >= 1000000) return `$${(val / 1000000).toFixed(0)}M`;
    return `$${val?.toLocaleString() || 0}`;
  };

  // Flow timeline from project stages
  const stageData = ['Stage 1', 'Stage 2', 'Stage 3', 'Stage 4', 'Stage 5'].map(stage => {
    const stageNum = parseInt(stage.split(' ')[1]);
    const stageProjects = projects.filter(p => p.stage_gate === stageNum);
    return stageProjects.reduce((acc, p) => acc + (p.budget_total || 0), 0);
  });

  const flowTimelineOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' },
      formatter: (params) => {
        const p = params[0];
        return `${p.name}: ${formatCurrency(p.value)}`;
      }
    },
    xAxis: {
      type: 'category',
      data: ['Stage 1', 'Stage 2', 'Stage 3', 'Stage 4', 'Stage 5'],
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8', rotate: 15 }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { 
        color: '#94a3b8',
        formatter: (val) => formatCurrency(val)
      }
    },
    series: [{
      data: stageData,
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 12,
      lineStyle: { color: '#c9a227', width: 3 },
      itemStyle: { color: '#c9a227', borderColor: '#0b1c2d', borderWidth: 3 },
      areaStyle: {
        color: {
          type: 'linear',
          x: 0, y: 0, x2: 0, y2: 1,
          colorStops: [
            { offset: 0, color: 'rgba(201, 162, 39, 0.4)' },
            { offset: 1, color: 'rgba(201, 162, 39, 0)' }
          ]
        }
      }
    }]
  };

  const cashFlowOptions = {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(15, 23, 42, 0.95)',
      borderColor: 'rgba(201, 162, 39, 0.3)',
      textStyle: { color: '#f8fafc' }
    },
    legend: {
      data: ['Inflow', 'Outflow'],
      textStyle: { color: '#94a3b8' },
      top: 0
    },
    grid: { left: '3%', right: '4%', bottom: '3%', top: '15%', containLabel: true },
    xAxis: {
      type: 'category',
      data: ['Q1', 'Q2', 'Q3', 'Q4'],
      axisLine: { lineStyle: { color: '#334155' } },
      axisLabel: { color: '#94a3b8' }
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      splitLine: { lineStyle: { color: '#1e293b' } },
      axisLabel: { color: '#94a3b8' }
    },
    series: [
      {
        name: 'Inflow',
        type: 'bar',
        data: [450, 520, 480, 550],
        itemStyle: { color: '#22c55e', borderRadius: [4, 4, 0, 0] }
      },
      {
        name: 'Outflow',
        type: 'bar',
        data: [380, 420, 450, 480],
        itemStyle: { color: '#ef4444', borderRadius: [4, 4, 0, 0] }
      }
    ]
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-[#c9a227] text-xl">Loading funds dashboard...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="funds-dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
          M1 Funds & Liquidity Dashboard
        </h1>
        <p className="text-slate-400 mt-1">Monitor fund flows, liquidity events, and disbursement readiness</p>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Total Budget</p>
                <p className="text-3xl font-bold text-[#c9a227] mt-2">{formatCurrency(totalBudget)}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-[#c9a227]/10 flex items-center justify-center">
                <PiggyBank className="w-6 h-6 text-[#c9a227]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Capital Deployed</p>
                <p className="text-3xl font-bold text-blue-400 mt-2">{formatCurrency(totalSpent)}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-blue-500/10 flex items-center justify-center">
                <ArrowRightLeft className="w-6 h-6 text-blue-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Available Funds</p>
                <p className="text-3xl font-bold text-purple-400 mt-2">{formatCurrency(availableFunds)}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-purple-500/10 flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-purple-400" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-slate-400 uppercase tracking-wider">Active Projects</p>
                <p className="text-3xl font-bold text-green-400 mt-2">{projects.filter(p => p.status === 'active').length}</p>
              </div>
              <div className="w-12 h-12 rounded-sm bg-green-500/10 flex items-center justify-center">
                <Banknote className="w-6 h-6 text-green-400" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Flow Timeline */}
      <Card className="glass-card">
        <CardHeader>
          <CardTitle className="text-slate-200">Fund Flow Timeline</CardTitle>
          <p className="text-sm text-slate-400">Deposits → Allocations → Trades → Liquidity → Disbursements</p>
        </CardHeader>
        <CardContent>
          <ReactECharts option={flowTimelineOptions} style={{ height: '300px' }} />
        </CardContent>
      </Card>

      {/* Charts & Funds List */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Cash Flow Analysis</CardTitle>
          </CardHeader>
          <CardContent>
            <ReactECharts option={cashFlowOptions} style={{ height: '300px' }} />
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Active Projects</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {projects?.slice(0, 4).map((project) => (
                <div key={project.id} className="p-4 bg-slate-800/30 rounded-sm">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-3">
                      <Wallet className="w-5 h-5 text-[#c9a227]" />
                      <div>
                        <p className="font-medium text-white">{project.name?.slice(0, 25)}...</p>
                        <p className="text-xs text-slate-500">{project.country}</p>
                      </div>
                    </div>
                    <Badge className="status-green">{project.status}</Badge>
                  </div>
                  <div className="grid grid-cols-3 gap-4 mt-3 text-sm">
                    <div>
                      <p className="text-slate-500">Budget</p>
                      <p className="text-[#c9a227] font-semibold">{formatCurrency(project.budget_total)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Spent</p>
                      <p className="text-blue-400 font-semibold">{formatCurrency(project.budget_spent)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Available</p>
                      <p className="text-green-400 font-semibold">{formatCurrency((project.budget_total || 0) - (project.budget_spent || 0))}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Disbursements & Audit */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="glass-card lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-slate-200">Project Disbursements</CardTitle>
          </CardHeader>
          <CardContent>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Project</th>
                  <th>Amount</th>
                  <th>Country</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {projects?.slice(0, 5).map((p, idx) => (
                  <tr key={idx}>
                    <td className="text-white">{p.name?.slice(0, 25)}...</td>
                    <td className="text-[#c9a227] font-semibold">{formatCurrency(p.budget_spent)}</td>
                    <td className="text-slate-400">{p.country}</td>
                    <td>
                      <Badge className={p.status === 'active' ? 'status-green' : 'status-yellow'}>{p.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="glass-card">
          <CardHeader>
            <CardTitle className="text-slate-200">Fund Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-green-500/10 rounded-sm border border-green-500/20">
                <CheckCircle className="w-6 h-6 text-green-400" />
                <div>
                  <p className="text-green-400 font-medium">Funds Healthy</p>
                  <p className="text-xs text-slate-400">All disbursements on track</p>
                </div>
              </div>
              <div className="text-sm text-slate-400 space-y-2">
                <div className="flex justify-between">
                  <span>Total Projects</span>
                  <span className="text-white">{projects.length}</span>
                </div>
                <div className="flex justify-between">
                  <span>Utilization</span>
                  <span className="text-white">{totalBudget > 0 ? Math.round(totalSpent / totalBudget * 100) : 0}%</span>
                </div>
                <div className="flex justify-between">
                  <span>Avg Project Budget</span>
                  <span className="text-[#c9a227]">{formatCurrency(projects.length > 0 ? totalBudget / projects.length : 0)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default FundsDashboard;
